/**
 * @module components/AmbientMode/scenes/InstitutionalScene
 * @description Escena institucional para el modo ambient (15 segundos por rotación).
 *
 * Muestra el estado de alto nivel de la organización en formato minimalista:
 * - Nombre de la organización y framework normativo activo
 * - Fecha y hora actual (locale es-CL)
 * - Porcentaje de cumplimiento global con semáforo de color
 * - Controles completados / total
 * - Indicador de tendencia (mejorando / estable / en riesgo)
 *
 * Diseñada como "pantalla de lobby" para mostrar el estado del SGSI
 * sin detalle técnico — apta para directivos y visitantes.
 *
 * @see SceneEngine que monta esta escena durante 15s antes de reiniciar el ciclo
 * @see useFramework para el nombre del framework activo
 */
import React, { memo, useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useMetrics } from '../../../contexts/DataContext';
import { useFramework } from '../../../contexts/FrameworkContext';

function formatDateTime(date: Date): string {
  return date.toLocaleString('es-CL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getStatusLabel(rate: number): { label: string; color: string } {
  if (rate >= 80) return { label: 'Cumplimiento Alto',    color: 'text-emerald-400' };
  if (rate >= 60) return { label: 'En Progreso',          color: 'text-blue-400'    };
  if (rate >= 40) return { label: 'Cumplimiento Parcial', color: 'text-yellow-400'  };
  return            { label: 'Requiere Atención',         color: 'text-red-400'     };
}

export const InstitutionalScene = memo(function InstitutionalScene() {
  const metrics = useMetrics();
  const { activeFramework } = useFramework();

  const [mounted, setMounted] = useState(false);
  const [now, setNow] = useState(new Date());

  // Fade-in escalonado
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  // Reloj ticking — pausa cuando pestaña oculta
  useEffect(() => {
    const tick = () => {
      if (!document.hidden) setNow(new Date());
    };
    const interval = setInterval(tick, 1_000);
    return () => clearInterval(interval);
  }, []);

  const rate = Math.round(metrics.controls.completionRate);
  const status = getStatusLabel(rate);
  const frameworkLabel = activeFramework?.name ?? 'ISO 27002:2022';

  const fadeBase = 'transition-opacity duration-1000';
  const visible  = mounted ? 'opacity-100' : 'opacity-0';

  return (
    <div className="flex flex-col items-center justify-center h-full gap-10 px-8 text-center select-none">

      {/* Shield con glow pulsante */}
      <div
        className={`${fadeBase} ${visible} ambient-glow-strong`}
        style={{ transitionDelay: '0ms' }}
      >
        <div className="animate-[ambientPulse_3s_ease-in-out_infinite]">
          <ShieldCheck className="w-44 h-44 text-blue-400" strokeWidth={1.2} />
        </div>
      </div>

      {/* Nombre del sistema */}
      <div
        className={`${fadeBase} ${visible}`}
        style={{ transitionDelay: '300ms' }}
      >
        <h1 className="text-8xl font-bold tracking-widest text-white uppercase">
          ACSGC
        </h1>
        <p className="mt-3 text-2xl text-gray-400 tracking-wide">
          Sistema de Gestión de Cumplimiento y Ciberseguridad
        </p>
      </div>

      {/* Separador */}
      <div
        className={`${fadeBase} ${visible} w-96 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent`}
        style={{ transitionDelay: '500ms' }}
      />

      {/* Framework + Estado */}
      <div
        className={`${fadeBase} ${visible} flex flex-col items-center gap-5`}
        style={{ transitionDelay: '700ms' }}
      >
        <span className="px-6 py-2 rounded-full border border-blue-500/40 bg-blue-500/10 text-blue-300 text-lg tracking-widest uppercase">
          {frameworkLabel}
        </span>

        <div className="flex items-center gap-5 mt-1">
          <span className="text-7xl font-mono font-bold text-white">
            {rate}%
          </span>
          <div className="flex flex-col items-start">
            <span className={`text-xl font-medium ${status.color}`}>{status.label}</span>
            <span className="text-base text-gray-500 mt-1">
              {metrics.controls.completed} / {metrics.controls.total} controles
            </span>
          </div>
        </div>
      </div>

      {/* Separador */}
      <div
        className={`${fadeBase} ${visible} w-96 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent`}
        style={{ transitionDelay: '900ms' }}
      />

      {/* Propósito institucional */}
      <div
        className={`${fadeBase} ${visible} max-w-2xl`}
        style={{ transitionDelay: '1100ms' }}
      >
        <p className="text-gray-400 text-xl leading-relaxed italic">
          "Monitoreo continuo del cumplimiento normativo y la gestión del riesgo
          de la información en el sector salud."
        </p>
      </div>

      {/* Timestamp */}
      <div
        className={`${fadeBase} ${visible} text-gray-600 text-base font-mono capitalize`}
        style={{ transitionDelay: '1300ms' }}
      >
        {formatDateTime(now)}
      </div>
    </div>
  );
});
